import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { client } from '../api';
import { useAuth } from '../auth';
import Avatar from '../components/Avatar';
import { roleLabel } from '../components/roleLabels';

function addressLine(addr) {
  if (!addr) return null;
  return [addr.line1, addr.line2, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || null;
}

export default function Account() {
  const { user: ctxUser } = useAuth();
  const [user, setUser] = useState(ctxUser);
  const [loading, setLoading] = useState(true);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState(ctxUser?.phone || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    client.get('/api/auth/me')
      .then((res) => { if (alive) { setUser(res.data.user); setPhone(res.data.user.phone || ''); } })
      .catch(() => { /* fall back to the context user */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (!user) return null;

  const roles = user.roles?.length ? user.roles : [user.role].filter(Boolean);
  const savedAddress = addressLine(user.savedAddress);
  const savePhone = async (event) => {
    event.preventDefault(); setSaving(true); setError('');
    try { const res = await client.patch('/api/auth/me', { phone: phone.trim() }); setUser(res.data.user); setPhone(res.data.user.phone || ''); setEditingPhone(false); }
    catch (err) { setError(err.response?.data?.msg || 'Could not save your phone number.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="py-4" style={{ maxWidth: 640 }}>
      <h3 className="mb-4">Account details</h3>
      <Card>
        <Card.Body>
          <div className="d-flex align-items-center gap-3 mb-4">
            <Avatar name={user.name} email={user.email} size={56} />
            <div>
              <div className="h5 mb-0">{user.name}</div>
              <div className="text-muted">{user.email}</div>
            </div>
          </div>
          {error && <Alert variant="danger">{error}</Alert>}
          <dl className="row mb-0">
            <dt className="col-sm-4 fw-normal text-muted">Phone</dt>
            <dd className="col-sm-8">{editingPhone ? <Form onSubmit={savePhone} className="d-flex gap-2"><Form.Control aria-label="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} required autoFocus /><Button type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button><Button type="button" size="sm" variant="outline-secondary" onClick={() => { setPhone(user.phone || ''); setEditingPhone(false); setError(''); }}>Cancel</Button></Form> : <>{user.phone || <span className="text-muted">Not provided</span>} <Button size="sm" variant="link" className="p-0 ms-2 align-baseline" onClick={() => setEditingPhone(true)}>{user.phone ? 'Edit' : 'Add phone'}</Button></>}</dd>

            <dt className="col-sm-4 fw-normal text-muted">{roles.length > 1 ? 'Roles' : 'Role'}</dt>
            <dd className="col-sm-8 d-flex flex-wrap gap-1">
              {roles.map((r) => <span key={r} className="badge bg-secondary">{roleLabel(r)}</span>)}
            </dd>

            <dt className="col-sm-4 fw-normal text-muted">Account status</dt>
            <dd className="col-sm-8 text-capitalize">{user.status || 'active'}</dd>

            {savedAddress && (
              <>
                <dt className="col-sm-4 fw-normal text-muted">Last delivery address</dt>
                <dd className="col-sm-8">{savedAddress}</dd>
              </>
            )}
          </dl>
          {loading && (
            <div className="text-muted small mt-3">
              <Spinner as="span" size="sm" animation="border" className="me-2" />Refreshing&hellip;
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
